import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import {
  Heart,
  Stethoscope,
  HandHeart,
  CheckCircle2,
  ArrowRight,
  Users,
  Target
} from 'lucide-react';
import { mockData } from '../mock';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Pledge = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState('donor');
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [pledgeForm, setPledgeForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    organization: '',
    specialization: '',
    pledgeAmount: '',
    availableHours: '',
    skills: '',
    message: ''
  });

  const roles = [
    {
      id: 'donor',
      title: 'Donor',
      icon: Heart,
      description: 'Provide financial support to research initiatives',
      color: 'text-red-600',
      bgColor: 'bg-red-100'
    },
    {
      id: 'doctor',
      title: 'Medical Professional',
      icon: Stethoscope,
      description: 'Contribute medical expertise and research collaboration',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      id: 'volunteer',
      title: 'Volunteer',
      icon: HandHeart,
      description: 'Donate your time and skills to support our mission',
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    }
  ];

  const handleCampaignToggle = (campaignId) => {
    setSelectedCampaigns(prev => 
      prev.includes(campaignId)
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedCampaigns.length === 0) {
      toast({
        title: "Please Select Campaigns",
        description: "You must select at least one campaign to pledge your support.",
        variant: "destructive"
      });
      return;
    }

    try {
      await axios.post(`${API}/pledge`, {
        role: selectedRole,
        campaigns: selectedCampaigns.map(String),
        ...pledgeForm,
      });
      toast({
        title: "Pledge Submitted Successfully!",
        description: `Thank you for pledging your support as a ${selectedRole}. We'll be in touch soon.`,
      });
      setPledgeForm({
        fullName: '',
        email: '',
        phone: '',
        organization: '',
        specialization: '',
        pledgeAmount: '',
        availableHours: '',
        skills: '',
        message: ''
      });
      setSelectedCampaigns([]);
    } catch (err) {
      toast({
        title: "Could not submit pledge",
        description: err.response?.data?.detail || "Please try again shortly.",
        variant: "destructive"
      });
    }
  };

  const currentRole = roles.find(role => role.id === selectedRole);
  const RoleIcon = currentRole.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_4f329244-455b-4bf6-8c8b-a4ec61e13f95/artifacts/dly2iwzc_NPC%20Logo.png" 
              alt="Foundation Logo" 
              className="h-12 w-12 object-contain"
            />
            <div>
              <h1 className="text-lg font-bold text-gray-900">The Genevieve Fourie Teles Foundation</h1>
              <p className="text-xs text-gray-600">For Combating Diseases</p>
              <p className="text-xs text-gray-500 font-medium">NPC: 2025/918417/08</p>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <a href="/" className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">Home</a>
            <a href="/#about" className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">About</a>
            <a href="/#campaigns" className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">Campaigns</a>
            <a href="/#contact" className="text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">Contact</a>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => navigate('/')}>
              <Heart className="w-4 h-4 mr-2" />
              Donate Now
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <Badge className="bg-white/20 text-white hover:bg-white/30 border-0">
              <Target className="w-3 h-3 mr-1" />
              Make Your Commitment
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold">
              Pledge Your Support
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Join our community of dedicated doctors, volunteers, and donors making a real difference 
              in chronic pain research and patient advocacy.
            </p>
          </div>
        </div>
      </section>

      {/* Main Pledge Form */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <form onSubmit={handleSubmit}>
              {/* Role Selection */}
              <Card className="mb-8 border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl">Step 1: Choose Your Role</CardTitle>
                  <CardDescription>Select how you'd like to contribute to our mission</CardDescription>
                </CardHeader>
                <CardContent>
                  <RadioGroup value={selectedRole} onValueChange={setSelectedRole}>
                    <div className="grid md:grid-cols-3 gap-4">
                      {roles.map((role) => {
                        const Icon = role.icon;
                        return (
                          <label
                            key={role.id}
                            className={`cursor-pointer rounded-lg border-2 p-6 transition-all hover:shadow-md ${
                              selectedRole === role.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <RadioGroupItem value={role.id} id={role.id} className="mt-1" />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <div className={`w-10 h-10 rounded-lg ${role.bgColor} flex items-center justify-center`}>
                                    <Icon className={`w-5 h-5 ${role.color}`} />
                                  </div>
                                </div>
                                <h3 className="font-semibold text-gray-900 mb-1">{role.title}</h3>
                                <p className="text-sm text-gray-600">{role.description}</p>
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </RadioGroup>
                </CardContent>
              </Card>

              {/* Campaign Selection */}
              <Card className="mb-8 border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl">Step 2: Select Campaigns</CardTitle>
                  <CardDescription>Choose which research initiatives you want to support</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {mockData.campaigns.map((campaign) => {
                      const progress = Math.round((campaign.currentAmount / campaign.goalAmount) * 100);
                      return (
                        <label
                          key={campaign.id}
                          className={`cursor-pointer rounded-lg border-2 p-6 transition-all hover:shadow-md flex items-start gap-4 ${
                            selectedCampaigns.includes(campaign.id)
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Checkbox
                            checked={selectedCampaigns.includes(campaign.id)}
                            onCheckedChange={() => handleCampaignToggle(campaign.id)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-4 mb-2">
                              <div>
                                <h3 className="font-semibold text-gray-900 mb-1">{campaign.title}</h3>
                                <p className="text-sm text-gray-600">{campaign.description}</p>
                              </div>
                              <Badge className="bg-green-100 text-green-700 flex-shrink-0">{campaign.category}</Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm mt-3">
                              <span className="text-gray-600">Progress: {progress}%</span>
                              <span className="text-gray-400">•</span>
                              <span className="font-semibold text-gray-900">R{campaign.currentAmount.toLocaleString()}</span>
                              <span className="text-gray-600">of R{campaign.goalAmount.toLocaleString()}</span>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Personal Information */}
              <Card className="mb-8 border-0 shadow-lg">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${currentRole.bgColor} flex items-center justify-center`}>
                      <RoleIcon className={`w-5 h-5 ${currentRole.color}`} />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">Step 3: Your Information</CardTitle>
                      <CardDescription>Tell us about yourself as a {currentRole.title}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Common Fields */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="fullName">Full Name *</Label>
                      <Input
                        id="fullName"
                        placeholder="Your full name"
                        value={pledgeForm.fullName}
                        onChange={(e) => setPledgeForm({...pledgeForm, fullName: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={pledgeForm.email}
                        onChange={(e) => setPledgeForm({...pledgeForm, email: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+27 XX XXX XXXX"
                        value={pledgeForm.phone}
                        onChange={(e) => setPledgeForm({...pledgeForm, phone: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="organization">Organization / Institution</Label>
                      <Input
                        id="organization"
                        placeholder="Your organization (optional)"
                        value={pledgeForm.organization}
                        onChange={(e) => setPledgeForm({...pledgeForm, organization: e.target.value})}
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Role-Specific Fields */}
                  {selectedRole === 'doctor' && (
                    <div className="space-y-6">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-900 mb-2">Medical Professional Details</h4>
                        <p className="text-sm text-blue-700">Help us understand your area of expertise</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="specialization">Medical Specialization *</Label>
                        <Input
                          id="specialization"
                          placeholder="e.g., Pain Management, Rheumatology, Neurology"
                          value={pledgeForm.specialization}
                          onChange={(e) => setPledgeForm({...pledgeForm, specialization: e.target.value})}
                          required={selectedRole === 'doctor'}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="skills">Areas of Research Interest</Label>
                        <Textarea
                          id="skills"
                          placeholder="Describe your research interests and how you'd like to contribute..."
                          value={pledgeForm.skills}
                          onChange={(e) => setPledgeForm({...pledgeForm, skills: e.target.value})}
                          rows={4}
                        />
                      </div>
                    </div>
                  )}

                  {selectedRole === 'volunteer' && (
                    <div className="space-y-6">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-semibold text-green-900 mb-2">Volunteer Details</h4>
                        <p className="text-sm text-green-700">Tell us about your availability and skills</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="availableHours">Available Hours Per Month *</Label>
                        <Input
                          id="availableHours"
                          type="number"
                          placeholder="e.g., 10"
                          value={pledgeForm.availableHours}
                          onChange={(e) => setPledgeForm({...pledgeForm, availableHours: e.target.value})}
                          required={selectedRole === 'volunteer'}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="skills">Skills & Experience *</Label>
                        <Textarea
                          id="skills"
                          placeholder="List your skills (e.g., administration, event planning, social media, fundraising...)"
                          value={pledgeForm.skills}
                          onChange={(e) => setPledgeForm({...pledgeForm, skills: e.target.value})}
                          rows={4}
                          required={selectedRole === 'volunteer'}
                        />
                      </div>
                    </div>
                  )}

                  {selectedRole === 'donor' && (
                    <div className="space-y-6">
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h4 className="font-semibold text-red-900 mb-2">Donation Commitment</h4>
                        <p className="text-sm text-red-700">Indicate your intended contribution amount</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pledgeAmount">Pledge Amount (ZAR) *</Label>
                        <Input
                          id="pledgeAmount"
                          type="number"
                          placeholder="e.g., 1000"
                          value={pledgeForm.pledgeAmount}
                          onChange={(e) => setPledgeForm({...pledgeForm, pledgeAmount: e.target.value})}
                          required={selectedRole === 'donor'}
                        />
                        <p className="text-xs text-gray-500">This is a non-binding pledge. Payment details will be provided separately.</p>
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Additional Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message">Additional Message</Label>
                    <Textarea
                      id="message"
                      placeholder="Share why you want to support our mission or any questions you have..."
                      value={pledgeForm.message}
                      onChange={(e) => setPledgeForm({...pledgeForm, message: e.target.value})}
                      rows={5}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Submit Button */}
              <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-indigo-50">
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-start gap-4">
                      <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Ready to Make a Difference?</h3>
                        <p className="text-sm text-gray-600">
                          Your pledge helps us plan our research initiatives and build a community of committed supporters.
                        </p>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      size="lg" 
                      className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                      data-testid="pledge-submit-button"
                    >
                      Submit Pledge
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </form>
          </div>
        </div>
      </section>

      {/* Why Pledge Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Your Pledge Matters</h2>
              <p className="text-lg text-gray-600">
                Every pledge strengthens our ability to drive meaningful change in chronic pain research and advocacy.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-0 shadow-lg">
                <CardContent className="pt-6 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Build Community</h3>
                  <p className="text-sm text-gray-600">
                    Join a network of dedicated individuals working together to transform chronic pain care.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="pt-6 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Enable Planning</h3>
                  <p className="text-sm text-gray-600">
                    Your commitment helps us plan research initiatives and allocate resources effectively.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardContent className="pt-6 text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Create Impact</h3>
                  <p className="text-sm text-gray-600">
                    Every pledge translates into real-world improvements in patient care and medical coverage.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <p className="text-sm text-gray-400">
              &copy; {new Date().getFullYear()} The Genevieve Fourie Teles Foundation for Combating Diseases. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pledge;
